import type { Campaign, CampaignDetail, CarriedAnswer, CreateCampaign } from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { refreshLists } from '../../lib/list';

/** Renewal campaigns and carried-forward answers (task 69). */

export interface PreviewClient {
  customer_key: string;
  customer_name: string;
  renewal_date: string | null;
  days_to_renewal: number | null;
  lifecycle: string;
  latest_status: string | null;
  blocked: string | null;
}

const key = (t?: string) => ['campaigns', t] as const;

export function useCampaigns() {
  const { tenant } = useSession();
  return useQuery({ queryKey: key(tenant?.id), queryFn: () => api<Campaign[]>('GET', '/campaigns'), enabled: !!tenant }); // prettier-ignore
}

export function useCampaign(id: string | null) {
  const { tenant } = useSession();
  return useQuery({ queryKey: [...key(tenant?.id), id], queryFn: () => api<CampaignDetail>('GET', `/campaigns/${id}`), enabled: !!tenant && !!id }); // prettier-ignore
}

export function usePreview(query: string | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id), 'preview', query],
    queryFn: () => api<PreviewClient[]>('GET', `/campaigns/preview?query=${encodeURIComponent(query!)}`),
    enabled: !!tenant && query !== null,
  });
}

export function useCampaignChanges() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => {
    void qc.invalidateQueries({ queryKey: key(tenant?.id) });
    void refreshLists(qc);
  };
  return {
    create: useMutation({ mutationFn: (body: CreateCampaign) => api<CampaignDetail>('POST', '/campaigns', body), onSuccess: done }), // prettier-ignore
    add: useMutation({ mutationFn: ({ id, start }: { id: string; start: string[] }) => api<CampaignDetail>('POST', `/campaigns/${id}/clients`, { start }), onSuccess: done }), // prettier-ignore
    close: useMutation({ mutationFn: (id: string) => api<CampaignDetail>('POST', `/campaigns/${id}/close`), onSuccess: done }), // prettier-ignore
  };
}

export function useCarried(assessmentId: string) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['assessments', tenant?.id, assessmentId, 'carried'],
    queryFn: () => api<CarriedAnswer[]>('GET', `/assessments/${assessmentId}/carried`),
    enabled: !!tenant,
  });
}

export function useConfirmCarried(assessmentId: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (questions?: string[]) =>
      api<{ confirmed: string[]; waiting: string[] }>('POST', `/assessments/${assessmentId}/carried/confirm`, questions ? { questions } : {}), // prettier-ignore
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', tenant?.id, assessmentId] }),
  });
}
